import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import AuthImage from "@/components/AuthImage";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Plus, Trash2, Users, Upload } from "lucide-react";
import { toast } from "sonner";

const PROFESSIONS = [
  { value: "social_media_manager", label: "Social Media Manager" },
  { value: "designer", label: "Designer" },
  { value: "developer", label: "Developer" },
  { value: "copywriter", label: "Copywriter" },
  { value: "account_manager", label: "Account Manager" },
  { value: "videographer", label: "Videographer" },
  { value: "other", label: "Outra função" },
];

const PROFESSION_LABEL = Object.fromEntries(PROFESSIONS.map((p) => [p.value, p.label]));

export default function TeamManagement() {
  const [staff, setStaff] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([api.get("/staff"), api.get("/clients")]);
      setStaff(s.data);
      setClients(c.data);
    } catch {
      toast.error("Erro ao carregar equipa");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const removeStaff = async (id) => {
    if (!window.confirm("Remover este membro da equipa?")) return;
    try {
      await api.delete(`/staff/${id}`);
      toast.success("Membro removido");
      load();
    } catch {
      toast.error("Erro ao remover");
    }
  };

  return (
    <Layout
      subtitle="Equipa interna"
      actions={
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            onClick={() => navigate("/admin")}
            className="rounded-full border-[#3B2B23] text-[#3B2B23] hover:bg-[#EAE3DB] h-11 px-5 text-xs uppercase tracking-[0.2em]"
            data-testid="back-to-clients-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Clientes
          </Button>
          <StaffDialog
            open={createOpen}
            onOpenChange={setCreateOpen}
            clients={clients}
            onSaved={() => { setCreateOpen(false); load(); }}
            trigger={
              <Button
                className="rounded-full bg-[#3B2B23] hover:bg-[#2A1E18] text-[#F9F6F0] h-11 px-5 text-xs uppercase tracking-[0.2em]"
                data-testid="new-staff-button"
              >
                <Plus className="w-4 h-4 mr-2" /> Novo membro
              </Button>
            }
          />
        </div>
      }
    >
      <section>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl sm:text-3xl text-[#1A1412] flex items-center gap-3">
            <Users className="w-5 h-5 text-[#8B5E3C]" /> A minha equipa
          </h2>
          <span className="text-[10px] uppercase tracking-widest text-[#5C4A43]">{staff.length} membros</span>
        </div>

        {loading ? (
          <div className="text-[#5C4A43] py-12 text-center text-xs uppercase tracking-widest">A carregar…</div>
        ) : staff.length === 0 ? (
          <div className="card-soft p-12 text-center" data-testid="no-staff-empty">
            <div className="text-2xl text-[#1A1412]">Sem membros na equipa</div>
            <div className="mt-2 text-[#5C4A43] text-sm font-light">
              Adiciono membros e atribuo-lhes os clientes a que terão acesso.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {staff.map((m) => (
              <Card
                key={m.id}
                className="card-soft p-6 group hover:-translate-y-0.5 transition-all"
                data-testid={`staff-card-${m.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    {m.avatar_path ? (
                      <div className="w-12 h-12 rounded-full overflow-hidden bg-white border border-[#EAE3DB] flex-shrink-0">
                        <AuthImage path={m.avatar_path} className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[#C07C5B] text-white flex items-center justify-center text-lg font-bold flex-shrink-0">
                        {m.name?.[0]?.toUpperCase() || "?"}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-bold text-[#1A1412] tracking-wide truncate">{m.name}</div>
                      <div className="text-[10px] text-[#8B5E3C] mt-0.5 uppercase tracking-widest">
                        {PROFESSION_LABEL[m.profession] || m.profession || "Sem função"}
                      </div>
                      <div className="text-[10px] text-[#5C4A43] mt-0.5 font-mono truncate">{m.email}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => removeStaff(m.id)}
                    className="p-1.5 rounded-full hover:bg-[#FBE6E4] text-[#8A2A22] flex-shrink-0"
                    data-testid={`delete-staff-${m.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div className="mt-5 pt-5 border-t border-[#EAE3DB]">
                  <div className="text-[10px] uppercase tracking-widest text-[#5C4A43] mb-2">
                    Clientes atribuídos
                  </div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {(m.assigned_clients || []).length === 0 ? (
                      <span className="text-[11px] text-[#8B5E3C] italic">Sem clientes atribuídos.</span>
                    ) : (
                      m.assigned_clients.map((cid) => {
                        const c = clients.find((x) => x.id === cid);
                        return (
                          <span
                            key={cid}
                            className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-[#EAE3DB] text-[#3B2B23]"
                          >
                            {c?.name || cid.slice(0, 6)}
                          </span>
                        );
                      })
                    )}
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setEditing(m)}
                    className="rounded-full border-[#3B2B23] text-[#3B2B23] hover:bg-[#EAE3DB] h-9 px-4 text-[10px] uppercase tracking-widest"
                    data-testid={`edit-staff-${m.id}`}
                  >
                    Editar acessos
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      {editing && (
        <StaffDialog
          open={!!editing}
          onOpenChange={(v) => !v && setEditing(null)}
          clients={clients}
          staff={editing}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </Layout>
  );
}

function StaffDialog({ open, onOpenChange, clients, staff, onSaved, trigger }) {
  const isEdit = !!staff;
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    name: "", email: "", password: "", profession: "social_media_manager",
    assigned_clients: [], avatar_path: "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (staff) {
      setForm({
        name: staff.name || "",
        email: staff.email || "",
        password: "",
        profession: staff.profession || "social_media_manager",
        assigned_clients: staff.assigned_clients || [],
        avatar_path: staff.avatar_path || "",
      });
    } else if (open) {
      setForm({ name: "", email: "", password: "", profession: "social_media_manager", assigned_clients: [], avatar_path: "" });
    }
  }, [staff, open]);

  const handleAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload?kind=avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, avatar_path: data.path }));
    } catch {
      toast.error("Erro a carregar foto");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const toggleClient = (id) => {
    setForm((s) => ({
      ...s,
      assigned_clients: s.assigned_clients.includes(id)
        ? s.assigned_clients.filter((x) => x !== id)
        : [...s.assigned_clients, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        const payload = {
          name: form.name,
          profession: form.profession,
          assigned_clients: form.assigned_clients,
          avatar_path: form.avatar_path || null,
        };
        if (form.password) payload.password = form.password;
        await api.put(`/staff/${staff.id}`, payload);
        toast.success("Membro atualizado");
      } else {
        await api.post("/staff", form);
        toast.success("Membro adicionado");
      }
      onSaved && onSaved();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="bg-[#F9F6F0] border border-[#EAE3DB] rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl text-[#1A1412]">
            {isEdit ? `Editar ${staff.name}` : "Adicionar membro à equipa"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-4">
          {/* Avatar */}
          <div>
            <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Foto</Label>
            <div className="mt-2 flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-white border border-[#EAE3DB] flex items-center justify-center overflow-hidden flex-shrink-0">
                {form.avatar_path ? (
                  <AuthImage path={form.avatar_path} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-[10px] text-[#8B5E3C] uppercase tracking-widest">sem foto</span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  type="button" variant="outline"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="rounded-full border-[#3B2B23] text-[#3B2B23] hover:bg-[#EAE3DB] h-9 px-4 text-[10px] uppercase tracking-widest"
                  data-testid="upload-staff-avatar-button"
                >
                  <Upload className="w-3.5 h-3.5 mr-2" />
                  {uploading ? "A enviar…" : (form.avatar_path ? "Trocar" : "Carregar")}
                </Button>
                {form.avatar_path && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, avatar_path: "" })}
                    className="text-[10px] text-[#8A2A22] hover:underline uppercase tracking-widest text-left"
                  >
                    Remover
                  </button>
                )}
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleAvatar} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Nome</Label>
              <Input
                required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-2 h-11 bg-white border-[#EAE3DB]"
                data-testid="staff-name-input"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Função</Label>
              <Select value={form.profession} onValueChange={(v) => setForm({ ...form, profession: v })}>
                <SelectTrigger className="mt-2 h-11 bg-white border-[#EAE3DB]" data-testid="staff-profession-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROFESSIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {!isEdit && (
            <div>
              <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Email</Label>
              <Input
                required type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2 h-11 bg-white border-[#EAE3DB]"
                data-testid="staff-email-input"
              />
            </div>
          )}
          <div>
            <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">
              {isEdit ? "Nova palavra-passe (deixe vazio para manter)" : "Palavra-passe"}
            </Label>
            <Input
              required={!isEdit}
              type="text" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-2 h-11 bg-white border-[#EAE3DB]"
              data-testid="staff-password-input"
            />
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Clientes atribuídos</Label>
            <p className="mt-1 text-[11px] text-[#5C4A43] font-light">
              Este membro só verá os dados dos clientes seleccionados.
            </p>
            <div className="mt-3 max-h-48 overflow-y-auto border border-[#EAE3DB] rounded-xl bg-white">
              {clients.length === 0 ? (
                <div className="p-4 text-[11px] text-[#8B5E3C] italic">Ainda não tenho clientes para atribuir.</div>
              ) : (
                clients.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-[#F9F6F0] cursor-pointer border-b border-[#EAE3DB] last:border-b-0"
                    data-testid={`staff-client-checkbox-${c.id}`}
                  >
                    <Checkbox
                      checked={form.assigned_clients.includes(c.id)}
                      onCheckedChange={() => toggleClient(c.id)}
                      className="border-[#8B5E3C] data-[state=checked]:bg-[#3B2B23] data-[state=checked]:border-[#3B2B23]"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-[#1A1412]">{c.name}</div>
                      <div className="text-[10px] text-[#5C4A43] uppercase tracking-widest">{c.company || c.email}</div>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button" variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-full border-[#3B2B23] text-[#3B2B23] hover:bg-[#EAE3DB]"
              data-testid="staff-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              type="submit" disabled={saving}
              className="rounded-full bg-[#3B2B23] hover:bg-[#2A1E18] text-[#F9F6F0]"
              data-testid="staff-save-button"
            >
              {saving ? "A guardar…" : (isEdit ? "Guardar alterações" : "Adicionar membro")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
